import { useContext, useEffect, useState } from 'react';
import { MaintenanceAPI } from '../api';
import { UserContext } from '../context/UserContext';
import './home.css';

export default function MentorProgram() {
  const { user } = useContext(UserContext);
  const [allowed, setAllowed] = useState(true); // default true (optimistic)
  const [checking, setChecking] = useState(true);
  const isAdmin = Array.isArray(user?.roles) && (user.roles.includes('admin') || user.roles.includes('owner'));

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const s = await MaintenanceAPI.status();
        const maintOn = !!s?.enabled;
        if (active) {
          // Wenn Wartungsmodus aktiv und kein Admin -> block
          if (maintOn && !isAdmin) setAllowed(false);
          else setAllowed(true);
        }
      } catch {
        if (active) setAllowed(true); // Im Fehlerfall nicht blockieren
      } finally {
        if (active) setChecking(false);
      }
    })();
    return () => { active = false; };
  }, [isAdmin]);

  if (checking) return null;
  if (!allowed) {
    return (
      <div className="home-page" style={{padding:'40px 20px', textAlign:'center'}}>
        <h2 style={{marginTop:0}}>Vorübergehend nicht verfügbar</h2>
        <p style={{maxWidth:640, margin:'12px auto', lineHeight:1.5}}>Diese Seite ist im Wartungsmodus nur für Administratoren sichtbar.</p>
      </div>
    );
  }

  return (
    <div className="home-page">
      {/* Hero */}
      <section className={user ? 'hero-offset-if-sidebar' : ''} style={{ width:'100vw', margin:0, padding:0 }}>
        <h2 className="hero-title" style={{ textAlign:'center', margin:'0 0 8px' }}>Mentoren‑Programm im AP‑Network</h2>
        <p className="hero-desc" style={{ textAlign:'center', margin:'0 auto 24px', maxWidth:760 }}>Gemeinsam wachsen. Echt unterstützt werden.</p>
        <p style={{ textAlign:'center', maxWidth:780, margin:'0 auto', color:'#D4D8DF' }}>
          Im AP‑Network stehen Creator nicht allein da. Unser Mentoren‑Programm bringt erfahrene Mitglieder mit neuen oder weniger erfahrenen Creatorn zusammen — für einen starken Start, schnelle Orientierung und nachhaltige Entwicklung.
        </p>
      </section>

      <section style={{ width:'100%', maxWidth:1100, margin:'0 auto', padding:'0 16px 60px', color:'#fff' }}>
        {/* Was ist das Programm */}
        <div className="highlight-card" style={{ padding:24, marginBottom:24 }}>
          <h2 className="highlight-title">Was ist das Mentoren‑Programm?</h2>
          <p style={{ color:'#cbd5e1', lineHeight:1.6 }}>Das Mentoren‑Programm ist ein freiwilliges Begleitangebot innerhalb des AP‑Network. Erfahrene Creator unterstützen Neueinsteiger oder Mitglieder, die gezielt Hilfe möchten — persönlich, praxisnah und auf Augenhöhe. Dabei geht es nicht um Bewertung, sondern um Starthilfe, Austausch und echte Community‑Kultur.</p>
        </div>

        {/* Was Mentoren tun */}
        <div className="highlight-card" style={{ padding:24, marginBottom:24 }}>
          <h2 className="highlight-title">Was Mentoren für dich tun</h2>
          <p style={{ color:'#cbd5e1', lineHeight:1.6 }}>Mentoren sind deine ersten Ansprechpartner im Network. Sie helfen dir zum Beispiel bei:</p>
          <div style={{ display:'grid', gap:16, gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', marginTop:12 }}>
            {[
              { t:'Einstieg ins AP‑Network', d:'Wie funktioniert das Network? Wo finde ich was? Was ist wichtig am Anfang?' },
              { t:'Plattform & Support‑System', d:'Erklärung von Punkten, Streamingplänen, Tools und Features.' },
              { t:'Streaming & Content‑Themen', d:'Setup-Fragen, Workflow, Ideen, Community‑Aufbau, Best Practices.' },
              { t:'Community‑Integration', d:'Events, Challenges, Mitmach‑Bereiche — und wie du dich schnell wohlfühlst.' }
            ].map((b,i)=>(
              <div key={i} style={{ border:'1px solid rgba(255,255,255,0.10)', background:'rgba(255,255,255,0.03)', borderRadius:10, padding:14 }}>
                <div style={{ fontWeight:700, marginBottom:6 }}>{b.t}</div>
                <div style={{ fontSize:13, color:'#cbd5e1', lineHeight:1.5 }}>{b.d}</div>
              </div>
            ))}
          </div>
          <p style={{ marginTop:16, color:'#D4D8DF' }}>Kurz: Mentoren sorgen dafür, dass du nicht raten musst, sondern direkt loslegen kannst.</p>
        </div>

        {/* Ablauf */}
        <div className="highlight-card" style={{ padding:24, marginBottom:24 }}>
          <h2 className="highlight-title">So läuft Mentoring ab</h2>
          <div style={{ display:'grid', gap:10, color:'#cbd5e1', lineHeight:1.5, fontSize:14 }}>
            <div>Ein Mentoring dauert in der Regel ca. 4 Wochen.</div>
            <div>Du kannst dir einen Mentor selbst aussuchen oder bekommst auf Wunsch einen passenden Mentor zugeteilt.</div>
            <div>Mentoren betreuen maximal 3 Mentees gleichzeitig — damit genug Zeit für dich bleibt.</div>
            <div>Es gibt keine 24/7‑Pflicht: Mentoring ist zuverlässig, aber fair und gesund begrenzt.</div>
          </div>
        </div>

        {/* Wer kann Mentor werden */}
        <div className="highlight-card" style={{ padding:24, marginBottom:24 }}>
          <h2 className="highlight-title">Wer Mentor werden kann</h2>
          <p style={{ color:'#cbd5e1', lineHeight:1.6 }}>Mentoren sind engagierte Mitglieder aus der Community, die neue Creator gerne begleiten möchten. Du musst dafür kein Admin und kein Mod sein — im Gegenteil: Mentoring ist ausdrücklich für normale AP‑Network‑Mitglieder gedacht, die Erfahrung gesammelt haben und Lust haben, ihr Wissen weiterzugeben. Wichtig ist nicht ein Titel, sondern dein Beitrag zur Community.</p>
          <p style={{ color:'#cbd5e1', lineHeight:1.6 }}>Mentor werden kann, wer:</p>
          <div style={{ display:'grid', gap:10, color:'#cbd5e1', lineHeight:1.5, fontSize:14 }}>
            <div>schon länger aktiv im AP‑Network ist,</div>
            <div>sich zuverlässig beteiligt und positiv auffällt,</div>
            <div>die Werte des Networks lebt,</div>
            <div>und gerne neue Leute unterstützt.</div>
          </div>
          <p style={{ color:'#cbd5e1', lineHeight:1.6 }}>Admin‑ oder Mod‑Rollen schließen Mentoring nicht aus — wer zusätzlich zu seinen Rechten auch begleiten möchte, kann ebenfalls Mentor sein. Aber Mentoring selbst bleibt eine eigene Community‑Aufgabe, unabhängig vom Rang. Die Bewerbung erfolgt direkt über die Plattform.</p>
        </div>

        {/* Warum lohnt es sich */}
        <div className="highlight-card" style={{ padding:24, marginBottom:24 }}>
          <h2 className="highlight-title">Warum sich Mentoring lohnt</h2>
          <div style={{display:'grid', gap:16, gridTemplateColumns:'repeat(auto-fit,minmax(260px,1fr))', marginTop:12}}>
            <BenefitBox title="Für Mentees">
              schneller Einstieg · klare Orientierung · persönliche Unterstützung · bessere Entwicklung durch Feedback & Erfahrungstransfer
            </BenefitBox>
            <BenefitBox title="Für Mentoren">
              sichtbare Anerkennung im Network · Mentor‑Badge & Rolle · Punkte‑Bonus für aktive Betreuung · Mentor‑Achievements / Stufen
            </BenefitBox>
            <BenefitBox title="Für die Community">
              mehr Zusammenhalt · höhere Qualität · nachhaltiges Wachstum · echte "wir ziehen gemeinsam hoch"‑Kultur
            </BenefitBox>
            <BenefitBox title="Für das Netzwerk">
              Wachstum & Qualität · attraktiv für neue Partner · geringe Fluktuation · Vernetzung der Creator untereinander
            </BenefitBox>
          </div>
        </div>

        {/* Call to Action */}
        <div className="highlight-card" style={{ padding:24 }}>
          <h2 className="highlight-title">Du möchtest einen Mentor?</h2>
          <p style={{ color:'#cbd5e1', lineHeight:1.6 }}>Dann starte einfach dein Mentoring über dein Profil auf der Plattform oder melde dich im Community‑Bereich. Wir matchen dich mit einem Mentor, der zu dir passt — oder du wählst selbst.</p>
          <p style={{ color:'#D4D8DF', fontWeight:600, marginTop:12 }}>Willkommen im AP‑Network. Hier wächst niemand allein.</p>
          <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginTop:16 }}>
            <a href="/register" className="hero-btn-primary" style={{ textDecoration:'none', padding:'12px 18px' }}>Jetzt beitreten</a>
            <a href="/kontakt" className="hero-btn-secondary" style={{ textDecoration:'none', padding:'12px 18px' }}>Kontakt aufnehmen</a>
          </div>
          <div style={{ marginTop:20 }}>
            <a href="#" onClick={(e)=>{e.preventDefault(); window.scrollTo({top:0,behavior:'smooth'});}} style={{ color:'#32CD89', fontSize:13 }}>Nach oben ↑</a>
          </div>
        </div>
      </section>
    </div>
  );
}

function BenefitBox({ title, children }) {
  return (
    <div style={{ border:'1px solid rgba(255,255,255,0.10)', background:'rgba(255,255,255,0.03)', borderRadius:10, padding:16 }}>
      <div style={{ fontWeight:700, marginBottom:8, color:'#E5E7EB' }}>{title}</div>
      <div style={{ fontSize:13, color:'#cbd5e1', lineHeight:1.5 }}>{children}</div>
    </div>
  );
}
